import { HttpService } from "./https.service";
import { BASE_URL } from "../../config/config";

const http = new HttpService();

// Admin-dashboard, account-wide view of the same Trackier publisher account
// the influencer-facing CashkaroService scopes per influencer. Only one
// Trackier publisher account exists for the whole platform, so nothing here
// is influencer-scoped — reports/conversions are already the full picture,
// and "access" is requested once, account-wide.
//
// Every method takes userId: the backend gates this whole module behind the
// view_publisher_manager role flag (see publisher.routes.ts's router-level
// check), and — same convention as the rest of this codebase — trusts the
// user_id the client supplies rather than deriving it from the JWT, so it
// must be sent on every call or the backend gate has nothing to check.
export class AdminPublisherService {
  static async getProfile(userId: number): Promise<any> {
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/profile?user_id=${userId}`, null);
  }

  static async getCampaigns(userId: number, params: { search?: string; category?: string } = {}): Promise<any> {
    const query = new URLSearchParams(
      Object.entries({ ...params, user_id: userId }).reduce((acc: Record<string, string>, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/campaigns?${query}`, null);
  }

  static async getCampaignDetail(userId: number, campaignId: number | string): Promise<any> {
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/campaigns/${campaignId}?user_id=${userId}`, null);
  }

  static async requestCampaignAccess(userId: number, campaignId: number | string): Promise<any> {
    return http.postRequest(`${BASE_URL}/admin-dashboard/publisher/campaigns/${campaignId}/access`, { user_id: userId }, null);
  }

  static async getCategories(userId: number): Promise<any> {
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/categories?user_id=${userId}`, null);
  }

  static async getCoupons(userId: number, campaignId?: number | string): Promise<any> {
    const query = new URLSearchParams({ user_id: String(userId), ...(campaignId ? { campaign_id: String(campaignId) } : {}) }).toString();
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/coupons?${query}`, null);
  }

  static async getDeals(userId: number): Promise<any> {
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/deals?user_id=${userId}`, null);
  }

  static async getConversions(
    userId: number,
    params: { start?: string; end?: string; status?: string; page?: number; limit?: number } = {}
  ): Promise<any> {
    const query = new URLSearchParams(
      Object.entries({ ...params, user_id: userId }).reduce((acc: Record<string, string>, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") acc[key] = String(value);
        return acc;
      }, {})
    ).toString();
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/conversions?${query}`, null);
  }

  // group accepts multiple dimensions, e.g. ["campaign_id","campaign_name"] or
  // ["source","campaign_id"] to see performance broken out by influencer.
  static async getReports(userId: number, params: { start?: string; end?: string; group?: string[] }): Promise<any> {
    const usp = new URLSearchParams();
    usp.set("user_id", String(userId));
    if (params.start) usp.set("start", params.start);
    if (params.end) usp.set("end", params.end);
    for (const g of params.group || []) usp.append("group", g);
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/reports?${usp.toString()}`, null);
  }

  // Brand-submitted Affiliate Deals campaign link requests awaiting review — Trackier has no
  // advertiser/ownership field, so this human review step is the only real check that a
  // brand is linking a campaign that's actually theirs.
  static async getCampaignLinkRequests(userId: number, status: string = "pending"): Promise<any> {
    const query = new URLSearchParams({ user_id: String(userId), status }).toString();
    return http.getRequest(`${BASE_URL}/admin-dashboard/publisher/campaign-link-requests?${query}`, null);
  }

  static async reviewCampaignLinkRequest(userId: number, requestId: number, action: "approve" | "reject", rejectReason?: string): Promise<any> {
    return http.postRequest(
      `${BASE_URL}/admin-dashboard/publisher/campaign-link-requests/${requestId}/review`,
      { user_id: userId, action, reject_reason: rejectReason },
      null
    );
  }
}
